import toast from '@services/toast.service';
import { toggleThenaWidget, usePipelineNodes, useFavoriteNodes, useFavoriteDashboards, useFavoriteAnalyses, enterDraftMode } from '@stores/data.store';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { getPromptAnswer, runBuild } from '@services/alert/alert.service';
import PageStructure, { PageContent, Pane, PaneContent} from './PageStructure.component';
import { getErrorMessage } from '@services/errors.service';
import { getGroupValueForNodeType } from '@services/modeling.service';
import { DashboardIcon } from '@components/pipelineNodes/PipelineNodeIcon.component';
import { savePipelineNode, useIsInDraftMode, useProjectConfig, useSources } from "@stores/data.store";
import PipelineNodeList, { NodeList, SingleMeasureItem, SingleDashboardItem, SingleAnalysisItem, SingleNodeItem } from "@components/pipelineNodes/PipelineNodeList.component";
import { Modal, Tabs, Tab, Form } from "react-bootstrap";

import { useMeasures } from "@models/reportBuilder";
import { useAuthState } from "@services/auth/auth.service";
import HeaderAlert from "@components/alert/HeaderAlert.component";
import { useFeatureFlag } from '@services/featureFlags/featureFlag.service';
import PliableLoader from '@components/loaders/PliableLoader.component';


interface QuickstartButtonProps {
    iconClass: string;
    text: string;
    color: string;
    onClick: () => void;
}

const QuickstartButton: React.FC<QuickstartButtonProps> = ({ iconClass, text, color, onClick }) => {
    return (
        <button className="btn w-100 bg-white border d-flex flex-column align-items-center justify-content-center" onClick={onClick} style={{ minWidth: 100, minHeight: 100 }}>
            <i className={iconClass} style={{ fontSize: '2rem', marginBottom: '0.5rem', color: color }}></i>
            <span>{text}</span>
        </button>
    );
}

type FavoriteEntity = {
    entityType: 'node' | 'dashboard' | 'analysis';
    entityId: string;
    entity: any; // You might want to make this more specific with a union type
    label: string;
    updatedAt?: Date;
};

const HomePage = () => {
    const pipelineNodes = usePipelineNodes();
    const inDraftMode = useIsInDraftMode();
    const sources = useSources();
    const isLoading = pipelineNodes.isLoading
    const projectConfig = useProjectConfig();

    const bookedOnboardingCall = useMemo(() => {
        if (!projectConfig.data) {
            return false;
        }

        return !!projectConfig.data.onboarding_info?.onboardingEventLink;
    }, [projectConfig.dataUpdatedAt]);

    const sourcesToShow = useMemo(() => {
        if (!sources.data) {
            return [];
        }
        return sources.data.filter(s => s.type != 'flat_file');
    }, [sources.dataUpdatedAt]);

    const reportNodes = useMemo(() => {
        if (!pipelineNodes.data) {
            return [];
        }
        return pipelineNodes.data.filter(n => getGroupValueForNodeType(n.node_type) == 'REPORTING').sort((a, b) => a.label.localeCompare(b.label));
    }, [pipelineNodes.dataUpdatedAt]);

    const businessObjects = useMemo(() => {
        if (!pipelineNodes.data) {
            return [];
        }
        return pipelineNodes.data.filter(n => getGroupValueForNodeType(n.node_type) == 'DATA_MODELING').sort((a, b) => a.label.localeCompare(b.label));
    }, [pipelineNodes.dataUpdatedAt]);

    const dataSources = useMemo(() => {
        if (!pipelineNodes.data) {
            return [];
        }
        return pipelineNodes.data.filter(n => getGroupValueForNodeType(n.node_type) == 'SOURCE');
    }, [pipelineNodes.dataUpdatedAt]);

    const navigate = useNavigate();

    const [nodeViewMode, setNodeViewMode] = useState<'LIST' | 'COLUMNS' | 'DIAGRAM'>('COLUMNS');

    const [nodeSearch, setNodeSearch] = useState('');

    const checkForNodeSearchEnterKey = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key == 'Enter') {
            setNodeSearch(e.currentTarget.value);
        }
    }, []);

    const nodesToShowInList = useMemo(() => {
        if (!pipelineNodes.data) {
            return [];
        }
        return pipelineNodes.data.filter(n => n.label.toLowerCase().includes(nodeSearch.toLowerCase())).sort((a, b) => a.label.localeCompare(b.label));
    }, [pipelineNodes.dataUpdatedAt, nodeSearch]);

    const addNewReport = useCallback(async () => {
        const reportName = await getPromptAnswer('What do you want to name this report?', 'New Report');
        if (reportName) {
            try {
                const result = await savePipelineNode({
                    id: null,
                    node_type: 'REPORT',
                    name: reportName,
                    label: reportName,
                    description: '',
                    upstream_node_ids: [],
                    fields: [],
                    table_name: '',
                });
                navigate(`/node/${result.id}/config`);
            } catch (err) {
                toast('danger', 'Error', getErrorMessage(err));
            }
            
        }
    }, []);

    const [showWelcomeModal, setShowWelcomeModal] = useState(false);

    const measures = useMeasures();

    const measuresForList = useMemo(() => {
        if (!measures.data) {
            return [];
        }

        return [...measures.data].sort((a, b) => a.name.localeCompare(b.name));
    }, [measures.dataUpdatedAt]);

    useEffect(() => {
        if (businessObjects.length == 0) {
            setShowWelcomeModal(true);
        }
    }, [businessObjects]);

    // if (pipelineNodes.data && pipelineNodes.data.length == 0 && measures.data && measures.data.length == 0) {
    //     return <PageStructure>
    //         <PageContent>
    //             <div className="p-onboarding pt-5">
    //                 <div className="empty-state text-center">
    //                     <h1>Welcome to Pliable</h1>
    //                 </div>
    //             </div>
    //         </PageContent>
    //     </PageStructure>
    // }

    const authState = useAuthState();

    const [timesOnHomeScreen, setTimesOnHomeScreen] = useState(0);

    useEffect(() => {
        const currentTimes = localStorage.getItem('timesOnHomeScreen');
        const parsed = currentTimes ? parseInt(currentTimes, 10) : 0; 
        if (currentTimes) {
            setTimesOnHomeScreen(parsed);
        } else {
            setTimesOnHomeScreen(0);
        }

        localStorage.setItem('timesOnHomeScreen', ((parsed || 0) + 1).toString());
    }, []);

    

    const hasSampleData = useMemo(() => {
        return !!projectConfig.data?.onboarding_info?.sampleData
    }, [projectConfig.dataUpdatedAt]);


    const [showOnboardingWizard, setShowOnboardingWizard] = useState(true);
    const [onboardingStepsComplete, setOnboardingStepsComplete] = useState<{[step: string]: boolean}>({});

    useEffect(() => {
        if (hasSampleData) {
            const timesOnSnowflakeScreen = parseInt(localStorage.getItem('timesOnSnowflakePage') || '0', 10);
            const timesOnSemanticLayerPage = parseInt(localStorage.getItem('timesOnSemanticLayerPage') || '0', 10);
            const timesOnReportingPage = parseInt(localStorage.getItem('timesOnReportingPage') || '0', 10);

            setOnboardingStepsComplete({
                'sources': timesOnSnowflakeScreen >= 1,
                'semantic_layer': timesOnSemanticLayerPage >= 1,
                'analysis': timesOnReportingPage >= 1,
            });

            if (timesOnSnowflakeScreen >= 1 && timesOnSemanticLayerPage >= 1 && timesOnReportingPage >= 1) {
                setShowOnboardingWizard(false);
            }
        } else if (pipelineNodes.data && measures.data) {
            let entities = 0;
            let metrics = 0;
            let sources = 0;
            let reports = 0; 

            const ranAnalysis = parseInt(localStorage.getItem('timesRanAnalysis') || '0', 10);

            pipelineNodes.data.forEach(n => {
                if (n.node_type == 'DIMENSION') {
                    entities++;
                }
                if (n.node_type == 'SOURCE') {
                    sources++;
                }
                if (n.node_type == 'REPORT') {
                    reports++;
                }
            });

            metrics = measures.data.length;

            setOnboardingStepsComplete({
                'sources': sources > 0,
                'entities': entities > 0,
                'reports': reports > 0,
                'analysis': ranAnalysis > 0,
                'metrics': metrics > 0,
            });

            if (sources > 0 && entities > 0 && ranAnalysis > 0 && metrics > 0) {
                setShowOnboardingWizard(false);
            }
        }
    }, [hasSampleData, pipelineNodes.dataUpdatedAt, measures.dataUpdatedAt]);

    const inOnboarding = true;
    const isInTrial = projectConfig.data && ['paused', 'trialing'].includes(projectConfig.data.subscription_status as string);
    const daysLeftInTrial = (isInTrial) ? projectConfig.data.days_left_in_trial : 0;



    const headerAlertContent = useMemo(() => {
        // if (timesOnHomeScreen < 3 && !bookedOnboardingCall) {
        if (isInTrial && (daysLeftInTrial || 0) < 5) {
            return <HeaderAlert style={{maxWidth: '50%'}} red>
                <strong>You have {daysLeftInTrial} days left in your trial!</strong> <button className="btn btn-primary btn-xs ms-5" onClick={() => navigate('/settings#billing')}>Upgrade now</button> <button onClick={toggleThenaWidget} className="btn btn-pliable btn-xs">Chat with us</button>
            </HeaderAlert>
        }
        
        if (isInTrial && !bookedOnboardingCall) {
            return <HeaderAlert style={{maxWidth: '50%'}}>
                <p className="mb-0"><strong>Have you booked your onboarding call?</strong>&nbsp;
                    Did you know you get a free 1-on-1 call with a Pliable data modeling expert? <Link to="/onboarding-call">Schedule call now</Link>
                </p>
                
            </HeaderAlert>
        }

        if (timesOnHomeScreen >= 3 && isInTrial) {
            return <HeaderAlert style={{maxWidth: '50%'}} red>
                <strong>You have {daysLeftInTrial} days left in your trial!</strong> <button className="btn btn-primary btn-xs ms-5" onClick={() => navigate('/settings#billing')}>Upgrade now</button> <button onClick={toggleThenaWidget} className="btn btn-pliable btn-xs">Chat with us</button>
            </HeaderAlert>
        }
        return <HeaderAlert style={{maxWidth: '50%'}}>
            <strong>Need some help?</strong> Click on the chat button <i className="mdi mdi-chat"/> at the top right of your screen to talk to a real live human.
        </HeaderAlert>
    }, [inOnboarding, isInTrial, bookedOnboardingCall, timesOnHomeScreen, daysLeftInTrial]);

    const isInDraft = useIsInDraftMode();

    const [timesOnHomeScreenInThisMode, setTimesOnHomeScreenInThisMode] = useState(0);

    const [showModeModal, setShowModeModal] = useState(false);
    
    useEffect(() => {
        const key = isInDraft ? 'timesOnHomeScreenDraft' : 'timesOnHomeScreenProd';
        const currentTimes = localStorage.getItem(key);
        const parsed = currentTimes ? parseInt(currentTimes, 10) : 0; 
        if (currentTimes) {
            setTimesOnHomeScreenInThisMode(parsed);
        } else {
            setTimesOnHomeScreenInThisMode(0);
        }

        if (parsed == 1) {
            setShowModeModal(true);
        }

        localStorage.setItem(key, ((parsed || 0) + 1).toString());
    }, [isInDraft]);

    const { tab } = useParams<{tab: string}>();
    
    const activeTab = tab || 'sources';
    const setActiveTab = useCallback((tab: string) => {
        navigate(`/home/t/${tab}`);
    }, []);

    const favoriteNodes = useFavoriteNodes();
    const favoriteDashboards = useFavoriteDashboards();
    const favoriteAnalyses = useFavoriteAnalyses();

    const favoriteThings = useMemo<FavoriteEntity[]>(() => {
        const things: FavoriteEntity[] = [];

        // Add nodes
        favoriteNodes.forEach(node => {
            things.push({
                entityType: 'node',
                entityId: node.id as string,
                entity: node,
                label: node.label,
            });
        });

        // Add dashboards
        favoriteDashboards.forEach(dashboard => {
            things.push({
                entityType: 'dashboard',
                entityId: dashboard.id as string,
                entity: dashboard,
                label: dashboard.name,
            });
        });

        // Add analyses
        favoriteAnalyses.forEach(analysis => {
            things.push({
                entityType: 'analysis',
                entityId: analysis.id as string,
                entity: analysis,
                label: analysis.name,
            });
        });

        // Sort by most recently updated
        return things.sort((a, b) => {
            if (!a.updatedAt) return 1;
            if (!b.updatedAt) return -1;
            return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
        });
    }, [favoriteNodes, favoriteDashboards, favoriteAnalyses]);

    const [aiQuestion, setAiQuestion] = useState('');

    const [showEnteringDraftModeModal, setShowEnteringDraftModeModal] = useState(false);

    const confirmDraftModeAndNavigate = useCallback(async (path: string) => {
        if (inDraftMode) {
            navigate(path);
        } else {
            setShowEnteringDraftModeModal(true);
            await enterDraftMode('quickstart');
            setTimeout(() => {
                setShowEnteringDraftModeModal(false);
                navigate(path);
            }, 3000);   
        }
    }, [inDraftMode]);

    const aiAssistantEnabled = useFeatureFlag('ai-assistant', false);




    const top = <>
        <Modal show={showEnteringDraftModeModal} onHide={() => setShowEnteringDraftModeModal(false)}>
            <Modal.Header closeButton>
                <Modal.Title>Entering Develop Mode...</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div className="d-flex center-vertically mb-3 justify-content-center"><PliableLoader size="sm"></PliableLoader>
                </div>
                <p className="text-center">This will allow you to make changes to your Pliable data, charts, and dashboards. Changes you make here are not visible to end users until you publish to production and run a build.</p>
            </Modal.Body>
        </Modal>
        <div className="bg-white border-bottom p-4">

            <h1 className="text-center">
                Welcome, {authState?.name}<br/>
                <span className="fw-normal">What do you want to do today?</span>
            </h1>
            {aiAssistantEnabled && <>
            <div className="my-4 d-flex justify-content-center">
                <form
                    className="w-100 border rounded p-3 bg-light"
                    style={{ maxWidth: 600 }}
                    onSubmit={e => {
                        e.preventDefault();
                        if (aiQuestion.trim()) {
                            navigate(`/ellie?initialQuestion=${encodeURIComponent(aiQuestion)}`);
                        }
                    }}
                >
                    <h3 className="text-center">Ask Ellie, your AI analyst</h3>
                    <div className="mb-2 d-flex center-vertically justify-content-center">
                        <input
                            className="form-control"
                            placeholder="Chat with Ellie"
                            style={{ fontSize: '1.2rem', width: '500px', display: 'inline-block' }}
                            value={aiQuestion}
                            onChange={e => setAiQuestion(e.target.value)}
                        />
                        <button
                            type="submit"
                            className="btn btn-pliable ms-2"
                            style={{fontSize: '1.1rem'}}
                            disabled={!aiQuestion.trim()}
                        >
                            Ask
                        </button>
                    </div>
                </form>
            </div>
            </>}
            <div className="row">
                <div className="col-3">
                    <QuickstartButton iconClass="mdi mdi-view-dashboard" text="Create a Dashboard" color="var(--pliable-blue)" onClick={() => confirmDraftModeAndNavigate('/dashboards/new')} />
                </div>
                <div className="col-3">
                    <QuickstartButton iconClass="mdi mdi-chart-bar" text="Run an Exploratory Analysis" color="var(--pliable-purple)" onClick={() => navigate('/charts/new')} />
                </div>
                <div className="col-3">
                    <QuickstartButton iconClass="mdi mdi-sitemap" text={inDraftMode ? 'Update your Data Model' : 'View your Data Model'} color="var(--pliable-green)" onClick={() => navigate('/model/visual')} />
                </div>
                <div className="col-3">
                    <QuickstartButton iconClass="mdi mdi-database" text="Connect a New Data Source" color="var(--pliable-yellow)" onClick={() => confirmDraftModeAndNavigate('/wizard/data-source')} />
                </div>
            </div>



        </div>
    </>
    return <PageStructure>
        
        <Modal show={showModeModal} onHide={() => setShowModeModal(false)}>
            <Modal.Header closeButton>
                <Modal.Title>
                    {isInDraft ? 'You are in Develop Mode' : 'You are in Production Mode'}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <p>
                    {isInDraft && <>
                        This is where you can make changes to your data model and reports. Changes you make here are not visible to end users until you publish to production and run a build.
                    </>}
                    {!isInDraft && <>
                        This is where you can view your reports and data models and explore your data. Production Mode is read-only. To make changes, switch to Develop Mode.
                    </>}
                </p>
            </Modal.Body>
            <Modal.Footer>
                <button className="btn btn-primary" onClick={() => setShowModeModal(false)}>Got it</button>
            </Modal.Footer>
        </Modal>
        <PageContent>
            <Pane>
                <PaneContent>
                    {top}

                        <div className="p-4">
                            <h2>Favorites</h2>
                                <div className="row g-3">
                                    {favoriteThings.map(favorite => (
                                        <div key={`${favorite.entityType}-${favorite.entityId}`} className="col-4">
                                            {favorite.entityType === 'dashboard' && (
                                                <SingleDashboardItem
                                                    dashboard={favorite.entity}
                                                    rounded
                                                    onClick={() => navigate(`/dashboards/${favorite.entityId}`)}
                                                />
                                            )}
                                            {favorite.entityType === 'analysis' && (
                                                <SingleAnalysisItem
                                                    analysis={favorite.entity}
                                                    rounded
                                                    onClick={() => navigate(`/charts/${favorite.entityId}`)}
                                                />
                                            )}
                                            {favorite.entityType === 'node' && (
                                                <SingleNodeItem
                                                    node={favorite.entity}
                                                    rounded
                                                    onClick={() => navigate(`/node/${favorite.entityId}`)}
                                                />
                                            )}
                                        </div>
                                    ))}
                                    {favoriteThings.length === 0 && (
                                        <div className="col-12 text-center text-muted py-5">
                                            <i className="mdi mdi-star font-24 mb-2 d-block"></i>
                                            <p>No favorites yet. Star charts, dashboards, and nodes to see them here.</p>
                                        </div>
                                    )}
                                </div>
                                
                        </div>

                </PaneContent>
            </Pane>
            
            
        </PageContent>
        
        
    </PageStructure>
}

export default HomePage;
